import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { wellsLegendItemsAtom, wellsTransactionIdAtom } from "atoms/map/payload/wells";
import { atom, useAtomValue } from "jotai";
import { RootState } from "store/rootReducer";
import {
  selectObservedLegend,
  selectRememberFocusColors,
  selectRememberLegendObservation,
  useFocusColorHoldStore
} from "stores/focusColorHoldStore";
import {
  FocusColorResolution,
  buildFocusColorResolution
} from "utils/color/focusColorSync/buildFocusColors";

import useBetaFeatures from "hooks/useBetaFeatures";

import { useFocusTranslation } from "./useFocusTranslation";

// Stands in for the wells legend atom when this instance is switched off, so a
// disabled caller subscribes to nothing. This hook runs from every chart's
// legend, and the wells legend atom changes on every wells fetch: without this,
// each of those legends would re-render on a fetch it has no interest in.
const NO_LEGEND_ITEMS_ATOM = atom([]);
const NO_TRANSACTION_ID_ATOM = atom("");

// Stable stand-in for the locks slice while this instance is switched off, so
// the resolution memo doesn't see a new object every render.
const EMPTY_LOCKED_COLORS = {};

export interface UseFocusColorSyncParams {
  /**
   * Lets a caller opt out entirely. Charts pass false for anything that is not
   * a rig/frac chart: this hook runs from every chart's legend, and without
   * that the translation request would fire on chart screens with no rig or
   * frac surface to colour.
   */
  enabled?: boolean;
}

/**
 * The one hook every rig/frac colouring surface calls — the map adapter, the
 * layer details panel, and the rig/frac charts. Resolves the well focus
 * (group-by) property, the well legend, any colour locks and, when the
 * focus needs it, the operator-variant translation into a single
 * FocusColorResolution every surface reads identically, so they can never
 * disagree about what to paint (FR-12).
 *
 * Gated entirely behind the "Rig And Frac Layers" beta feature, including
 * the translation request — with the feature off this always returns the
 * frozen inactive singleton and issues no request (SC-6).
 */
export function useFocusColorSync(
  params: UseFocusColorSyncParams = {}
): FocusColorResolution {
  const { enabled = true } = params;
  const { hasFeature } = useBetaFeatures();
  const isFeatureEnabled = enabled && hasFeature("Rig And Frac Layers");

  // Optional chaining here, not a direct read: several existing map test
  // suites (e.g. MapboxWrapper.test.tsx) preload only the Redux slice fields
  // a given test cares about, so `groupBy` can arrive without a
  // `globalGroupBy` key at all in that harness. Real app state always has
  // it; this just keeps a partial test store from crashing every render
  // that happens to mount this hook.
  const rawProperty = useSelector(
    (state: RootState) => state.groupBy?.globalGroupBy?.property
  );
  // Gating the property itself (rather than short-circuiting the whole hook)
  // keeps every read below unconditional — no conditional hooks — while
  // guaranteeing buildFocusColorResolution's trigger lookup fails and the
  // translation query stays disabled whenever the feature is off.
  const property = isFeatureEnabled ? rawProperty : undefined;

  // Every read below is gated the same way the property is, so an instance with
  // the feature off (or a caller that opted out) subscribes to nothing that can
  // change under it. The optional chaining is for the same reason throughout:
  // several existing map and chart suites preload only the slice fields their
  // own test cares about, and this hook now mounts in far more of them than it
  // used to.
  const legendItems = useAtomValue(
    isFeatureEnabled ? wellsLegendItemsAtom : NO_LEGEND_ITEMS_ATOM
  );
  const lockedColors = useSelector((state: RootState) =>
    isFeatureEnabled ? state.app?.lockedColors : undefined
  );
  const basePalette = useSelector((state: RootState) =>
    isFeatureEnabled ? state.userSetting?.activeColorPalette : undefined
  );
  // A wells request is out, so the legend is about to be replaced.
  //
  // This is necessary but nowhere near sufficient on its own. The flag is not
  // raised when the focus changes: the map's refetch is debounced, so for that
  // window the property is already the new one while the legend still holds the
  // old one, and the flag is also written by the facility map's own fetch,
  // which says nothing about the wells legend. The observation below is what
  // actually decides whether the legend describes the current focus.
  const isWellsRequestInFlight = useSelector(
    (state: RootState) => isFeatureEnabled && state.filter?.isFiltering === true
  );

  const transactionId = useAtomValue(
    isFeatureEnabled ? wellsTransactionIdAtom : NO_TRANSACTION_ID_ATOM
  );
  const observedLegend = useFocusColorHoldStore(selectObservedLegend);
  const rememberLegendObservation = useFocusColorHoldStore(
    selectRememberLegendObservation
  );
  // Note which property the legend belongs to as it lands. Recording on the
  // transaction id (not on the property) is the whole point: a focus change
  // must leave the note alone so the legend it describes still reads as stale.
  useEffect(() => {
    if (!isFeatureEnabled || !transactionId) {
      return;
    }
    rememberLegendObservation(transactionId, property);
    // `property` is deliberately absent: this fires for a new legend, not for a
    // new focus over an existing one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureEnabled, transactionId, rememberLegendObservation]);

  // Only judge staleness against a note about THIS legend. An unobserved
  // legend (nothing mounted when it landed) counts as fresh, the same answer
  // this made before the note existed.
  const isLegendStale =
    !!transactionId &&
    observedLegend?.transactionId === transactionId &&
    observedLegend.property !== property;

  const {
    translations,
    allTranslations,
    status: translationStatus
  } = useFocusTranslation({ property });

  const resolution = useMemo(
    () =>
      buildFocusColorResolution({
        property,
        legendItems,
        lockedColors: lockedColors ?? EMPTY_LOCKED_COLORS,
        translations,
        allTranslations,
        translationStatus,
        isWellsRequestInFlight,
        isLegendStale,
        basePalette
      }),
    [
      property,
      legendItems,
      lockedColors,
      translations,
      allTranslations,
      translationStatus,
      isWellsRequestInFlight,
      isLegendStale,
      basePalette
    ]
  );

  // Remember what each attribute last resolved to, for the surfaces that hold
  // it through the next pending window. Written from an effect, never during
  // render: the store is shared, so a render-phase write would let a discarded
  // render leak into what every other surface paints. The store drops a write
  // that matches what it already holds, so the several mounted instances that
  // all reach this line on the same commit cost one update between them.
  const rememberFocusColors = useFocusColorHoldStore(selectRememberFocusColors);
  useEffect(() => {
    if (resolution.state !== "active" || !resolution.attribute) {
      return;
    }
    rememberFocusColors(resolution.attribute, {
      colors: resolution.colors,
      reservedColors: resolution.reservedColors,
      basePalette: resolution.basePalette,
      binOfKey: resolution.binOfKey,
      binColors: resolution.binColors,
      hasOthers: resolution.hasOthers,
      othersColor: resolution.othersColor
    });
  }, [resolution, rememberFocusColors]);

  return resolution;
}
